import React, { Component } from "react";
import type { ApolloClient } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { find } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { push } from "store/actions/router";
import { toast } from "react-toastify";
import queryString from "query-string";

import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { registerKYBUser } from "actions/adminActions";
import { getCompanyMembers } from "services/adminService";
import { wrapError } from "utils/errorHelper";
import type { GetMembersForCompanyQuery } from "types/graphql";
import type { RootState } from "types/state";

import IconSpinner from "components/IconSpinner";
// ControllerForm's own-props (ownerInfo: OwnerInfo, members: Member[],
// onSubmit) are tighter than the values this page passes in (the local Identity
// shape, the nullable CompanyMembers query result, and a registerKYBUser-typed
// onSubmit) — cast to a loose component type at the boundary.
import ControllerForm from "./ControllerForm";

// A single member as returned by getMembersForCompany.
type CompanyMember = NonNullable<
  NonNullable<GetMembersForCompanyQuery["getMembersForCompany"]>[number]
>;
type CompanyMembers = GetMembersForCompanyQuery["getMembersForCompany"];

// Loose props shape for the ControllerForm boundary cast (see import comment).
interface ControllerFormProps {
  isAdminPortal?: boolean;
  ownerInfo?: unknown;
  error?: string;
  isFetching?: boolean;
  companyId?: string;
  members?: CompanyMembers;
  onSubmit?: (updatedInfo: Parameters<typeof registerKYBUser>[1]) => void;
}
const ControllerFormTyped =
  ControllerForm as unknown as React.ComponentType<ControllerFormProps>;

const actions = [
  adminConstants.REGISTER_KYB_USER,
  adminConstants.GET_COMPANY_MEMBERS,
];
const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

// OwnProps passed into the connected component by its parent route element.
// companyId is always supplied by the parent (used as a required query var and
// in route template literals); memberId is resolved from search params in the
// wrapper below and merged into the redux-mapped value.
interface OwnProps {
  companyId: string;
  memberId?: string | null;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  memberId: ownProps.memberId,
  error: errorSelector(state),
  isFetching: loadingSelector(state),
});

const mapDispatchToProps = {
  registerKYBUser,
  // found-not-fixed: getCompanyMembers is a bare service (returns a Promise,
  // not a thunk/action creator) and `this.props.getCompanyMembers` is never
  // read — componentDidMount calls the imported service directly. It is dead
  // connect wiring; preserved here, cast so the object remains a valid
  // mapDispatchToProps without retyping the live registerKYBUser/push entries.
  getCompanyMembers: getCompanyMembers as unknown as (...args: unknown[]) => {
    type: string;
  },
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

// Props as seen by the inner Controller class: redux props, the Apollo client
// injected by withApollo, plus companyId/memberId passed down by the wrapper.
type Props = WithApolloClient<PropsFromRedux & OwnProps>;

// The form's identity shape. Initial values are all strings, but after a
// member is fetched it is spread from the (loosely typed) member + profile, so
// extra fields are allowed via the index signature.
interface Identity {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  ssn: string;
  role: string;
  ownershipStake: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
  [key: string]: unknown;
}

interface State {
  identity: Identity;
  submitted: boolean;
  loading: boolean;
  fetching: boolean;
  showAddressModal: boolean;
  fetchingAddress: boolean;
  validatedAddress: Record<string, unknown>;
  // initialized to null; set from lodash find() which may yield undefined
  member: CompanyMember | null | undefined;
  // written by setState in componentDidMount
  members?: CompanyMembers;
}

class Controller extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      identity: {
        firstName: "",
        lastName: "",
        email: "",
        dob: "",
        ssn: "",
        role: "",
        ownershipStake: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        phone: "",
      },
      submitted: false,
      loading: false,
      fetching: true,
      showAddressModal: false,
      fetchingAddress: false,
      validatedAddress: {},
      member: null,
    };
  }

  async componentDidMount() {
    const queryObj = queryString.parse(window.location.search);

    const members = await getCompanyMembers(
      this.props.client as ApolloClient<object>,
      this.props.companyId
    );
    if (queryObj.memberId) {
      this.setState({
        fetching: true,
      });

      const member = find(
        members,
        (member) => member!.id === queryObj.memberId
      );

      this.setState({
        member,
        identity: {
          ...member,
          ...member!.memberProfile,
          dob: member!.memberProfile!.dob,
        } as unknown as State["identity"],
        members,
        fetching: false,
      });
    } else {
      this.setState({ fetching: false, members });
    }
  }

  _submitUser = (updatedInfo: Parameters<typeof registerKYBUser>[1]) => {
    this.props
      .registerKYBUser(this.props.client as ApolloClient<object>, updatedInfo)
      .then((result) => {
        if (!result.success) return;
        const isEditing = !!this.state.member;
        const term = isEditing ? "edited" : "registered";
        toast.success(`Successfully ${term} controller/owner.`);
        this.props.push(`/employers/${this.props.companyId}/kyb/summary`);
      })
      .catch((err) => {
        toast.error(wrapError(err));
      });
  };

  render() {
    const { identity } = this.state;

    if (this.state.fetching) {
      return <IconSpinner centered />;
    }

    return (
      <div
        className="tab-pane"
        id="controller"
        role="tabpanel"
        aria-labelledby="controller_tab"
      >
        <div>
          <ControllerFormTyped
            isAdminPortal={true}
            ownerInfo={identity}
            error={this.props.error}
            isFetching={this.props.isFetching}
            companyId={this.props.companyId}
            members={this.state.members}
            onSubmit={this._submitUser}
          />
        </div>
      </div>
    );
  }
}

const Controller_WithParams = (props: Props) => {
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get("memberId");
  return <Controller {...props} memberId={memberId} />;
};

export default connector(withApollo<Props>(Controller_WithParams));
